import { useNavigate } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import 'modules/core/hooks/useModuleAvailability'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import hasFeature from 'modules/core/lib/hasFeature'
import { useAsyncCan } from 'modules/core/lib/useAsyncCan'

import configurator from 'modules/app_store/lib/SubscriptionAddonConfigurator'
import useFetchData from 'modules/billing/hooks/useFetchData'
import { useMarketplaceModulesPath } from 'modules/marketplace_modules/hooks/useMarketplaceModulesPath'

/**
 * In order to know if the connected supplements section is available, we need to verify several things:
 *
 * - When expenses paywalling is enabled, Reimburse Through Payroll feature must be available.
 * - When expenses paywalling is disabled, the expenses app must be installed
 */
function isExpensesPayrollFeatureAvailable(
  isReimburseThroughPayrollFeatureAvailable: boolean
): boolean {
  return hasFeature('dev_packaging_paywalls')
    ? isReimburseThroughPayrollFeatureAvailable
    : hasFeature('expenses_v2')
}

export function usePayrollBundleSettings() {
  const { loading: addonLoading, isNotFound, subscriptionAddon } = useFetchData('payroll_bundle')
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { getListPath } = useMarketplaceModulesPath()

  const {
    loading: loadingPayrollAvailability,
    featureAvailability: reimburseThroughPayrollFeatureAvailability,
  } = useFeatureAvailability(Features.expenses_reimburse_through_payroll)

  const isConnectedExpensesMappingsVisible =
    !loadingPayrollAvailability &&
    isExpensesPayrollFeatureAvailable(
      reimburseThroughPayrollFeatureAvailability?.available ?? false
    )

  const { loading: loadingPermission, value: canManagePayroll } =
    useAsyncCan('payroll.manage_payroll')

  useUnsafeEffect(() => {
    let config = null

    if (subscriptionAddon) {
      config = configurator.getSubscriptionAddonConfig(subscriptionAddon, navigation)
    }

    if (
      (config && !config.hasSettings) ||
      (!addonLoading && !subscriptionAddon?.attributes.enabled) ||
      (!loadingPermission && !canManagePayroll)
    ) {
      navigate(getListPath())
    }
  }, [addonLoading, loadingPermission, canManagePayroll, navigate, subscriptionAddon, navigation])

  return {
    isConnectedExpensesMappingsVisible,
    subscriptionAddon,
    isNotFound,
    loading: loadingPayrollAvailability,
  }
}
